import { useMemo } from 'react';

import { useTaskOrderStaffingOutlook } from './useQueries';

/*
 * The staffing outlook's numbers for a task order, shared by the outlook page and the
 * scenarios page so the two can never disagree about spend to date or the budget.
 *
 * Months that are over count actual hours, months still to come count the forecast,
 * and the current month counts whichever is higher so far (actuals only grow within a
 * month, so the forecast is the floor until someone logs past it).
 */

export const toMonthStart = (iso) => (iso ? `${String(iso).slice(0, 7)}-01` : null);

// Which figure a month counts: actuals once it is over, forecast before it starts, and
// the higher of the two while it is under way.
export const monthKind = (ms, currentMonth) => (ms < currentMonth ? 'actual' : ms === currentMonth ? 'current' : 'forecast');

const outlookHours = (cell, kind) => {
  if (!cell) return 0;
  const fc = Number(cell.forecastHours ?? 0);
  const ac = Number(cell.actualHours ?? 0);
  if (kind === 'actual') return ac;
  if (kind === 'current') return Math.max(ac, fc);
  return fc;
};

const outlookOdc = (entry, kind) => {
  if (!entry) return 0;
  const fc = Number(entry.forecast ?? 0);
  const ac = Number(entry.actual ?? 0);
  if (kind === 'actual') return ac;
  if (kind === 'current') return Math.max(ac, fc);
  return fc;
};

export default function useStaffingOutlookModel(projectId, taskOrderId) {
  const { data, isLoading, error } = useTaskOrderStaffingOutlook(
    Number.isInteger(projectId) ? projectId : null,
    Number.isInteger(taskOrderId) ? taskOrderId : null
  );

  const currentMonth = data?.currentMonth || '';
  const months = useMemo(() => (Array.isArray(data?.months) ? data.months : []), [data?.months]);
  const startMonth = toMonthStart(data?.startDate);
  const endMonth = toMonthStart(data?.endDate);
  // null means no budget anywhere; 0 is a real (if unusual) budget and is kept.
  const budget = data?.budget === null || data?.budget === undefined || !Number.isFinite(Number(data.budget))
    ? null
    : Number(data.budget);

  const model = useMemo(() => {
    const people = (Array.isArray(data?.people) ? data.people : []).map((p) => {
      const cells = {};
      let hours = 0;
      let dollars = 0;
      for (const ms of months) {
        const kind = monthKind(ms, currentMonth);
        const cell = p.months?.[ms];
        const h = outlookHours(cell, kind);
        const d = h * Number(cell?.rate ?? p.currentRate ?? 0);
        cells[ms] = { kind, hours: h, dollars: d, forecastHours: Number(cell?.forecastHours ?? 0), actualHours: Number(cell?.actualHours ?? 0) };
        hours += h;
        dollars += d;
      }
      return { ...p, cells, hours, dollars };
    });

    const odcByMonth = data?.odcByMonth || {};
    const odcCells = {};
    let odcTotal = 0;
    for (const ms of months) {
      const kind = monthKind(ms, currentMonth);
      const v = outlookOdc(odcByMonth[ms], kind);
      odcCells[ms] = { kind, dollars: v };
      odcTotal += v;
    }

    const monthTotals = {};
    let cumulative = 0;
    const series = [];
    for (const ms of months) {
      const kind = monthKind(ms, currentMonth);
      const hours = people.reduce((acc, p) => acc + p.cells[ms].hours, 0);
      const dollars = people.reduce((acc, p) => acc + p.cells[ms].dollars, 0) + odcCells[ms].dollars;
      cumulative += dollars;
      monthTotals[ms] = { hours, dollars, cumulative };
      series.push({ monthStart: ms, kind, monthly: dollars, cumulative });
    }

    const laborHours = people.reduce((acc, p) => acc + p.hours, 0);
    const laborDollars = people.reduce((acc, p) => acc + p.dollars, 0);
    const projected = laborDollars + odcTotal;

    // Spent so far: every closed month plus what has actually been logged this month.
    let spentToDate = 0;
    for (const p of people) {
      for (const ms of months) {
        if (ms > currentMonth) continue;
        const cell = p.months?.[ms];
        spentToDate += Number(cell?.actualHours ?? 0) * Number(cell?.rate ?? 0);
      }
    }
    for (const ms of months) if (ms <= currentMonth) spentToDate += Number(odcByMonth[ms]?.actual ?? 0);

    // Hours a month that would land the task order on its budget, priced at the blended
    // rate of the people still forecast (total dollars over total hours, so a busy senior
    // person pulls the rate up in proportion to the hours they actually carry).
    const lastPlanMonth = endMonth || months[months.length - 1] || currentMonth;
    const remainingMonths = months.filter((ms) => ms >= currentMonth && ms <= lastPlanMonth);
    const remLabor = people.reduce(
      (acc, p) => {
        for (const ms of remainingMonths) {
          acc.hours += p.cells[ms].hours;
          acc.dollars += p.cells[ms].dollars;
        }
        return acc;
      },
      { hours: 0, dollars: 0 }
    );
    const blendedRate = remLabor.hours > 0
      ? remLabor.dollars / remLabor.hours
      : laborHours > 0 ? laborDollars / laborHours : null;
    const gap = budget !== null ? budget - projected : null;
    const hoursPerMonth = gap !== null && blendedRate && remainingMonths.length > 0
      ? gap / (remainingMonths.length * blendedRate)
      : null;

    // Things worth a warning before anyone trusts the totals.
    const afterEnd = endMonth
      ? people
        .map((p) => ({
          name: p.name,
          hours: months.filter((ms) => ms > endMonth).reduce((acc, ms) => acc + p.cells[ms].hours, 0),
          dollars: months.filter((ms) => ms > endMonth).reduce((acc, ms) => acc + p.cells[ms].dollars, 0),
        }))
        .filter((r) => r.hours > 0)
      : [];
    const noRate = people
      .map((p) => ({
        name: p.name,
        hours: months.reduce((acc, ms) => acc + (Number(p.months?.[ms]?.rate ?? 0) > 0 ? 0 : p.cells[ms].hours), 0),
      }))
      .filter((r) => r.hours > 0);

    return {
      people,
      odcCells,
      odcTotal,
      monthTotals,
      series,
      laborHours,
      projected,
      spentToDate,
      remainingMonths,
      blendedRate,
      gap,
      hoursPerMonth,
      afterEnd,
      noRate,
    };
  }, [budget, currentMonth, data?.odcByMonth, data?.people, endMonth, months]);

  return { data, isLoading, error, model, currentMonth, months, startMonth, endMonth, budget };
}
